import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortalTarget } from '@/components/PortalTarget';

interface ImagePreviewProps {
  src?: string | null;
  alt?: string;
  className?: string;
  previewClassName?: string;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({
  src,
  alt = 'Preview',
  className = '',
  previewClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // The lightbox is `fixed inset-0`; inside a `<dialog>` opened with
  // `showModal()` it must live in that dialog, not in the inert page.
  const portalTarget = usePortalTarget();

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!src) return null;

  return (
    <>
      <button type="button" onClick={() => setIsOpen(true)} className="w-full">
        <img
          src={src}
          alt={alt}
          className={`cursor-zoom-in ${className}`.trim()}
        />
      </button>

      {isOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
            <button
              type="button"
              aria-label="Close preview"
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 cursor-zoom-out"
            />
            <div className="relative z-10 max-h-[85vh] max-w-[90vw]">
              <img
                src={src}
                alt={alt}
                className={`max-h-[85vh] max-w-[90vw] rounded-md object-contain shadow-lg ${
                  previewClassName || ''
                }`.trim()}
              />
            </div>
          </div>,
          portalTarget
        )}
    </>
  );
};

export default ImagePreview;
